import React from "react"
import PropTypes from "prop-types"
import { IntlContextConsumer } from "gatsby-plugin-react-intl"
import styled, { css } from "styled-components"
import { Link } from "gatsby"

import { media } from "../styles"

import { isBrowser, isMatchedIgnoredPaths } from "../helpers"

const languageName = {
  de: "DE",
  en: "EN",
}

const LangSwitcherWrapper = styled.nav``

const LangSwitcher = styled.ul`
  display: flex;
  flex-flow: row nowrap;
  gap: 7.5px;
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    margin: 0;
    padding: 0;
  }

  @media ${media.xl} {
    gap: 10px;
  }

  @media print {
    display: none;
  }
`

const LangLink = styled(Link)`
  color: var(--color-spaceblue);
  font-weight: 700;
  text-decoration: none;

  ${(props) =>
    props.$active &&
    css`
      color: var(--color-neutral-dark);
      cursor: default;
    `};
`

const CurrentLang = styled.span`
  color: var(--color-neutral-dark);
  cursor: default;
`

const LanguageSwitcher = ({ className }) => {
  const params = isBrowser()
    ? `${window.location.search}${window.location.hash}`
    : ``

  return (
    <IntlContextConsumer>
      {({
        defaultLanguage,
        language: currentLocale,
        languages,
        messages,
        ignoredPaths,
        originalPath,
        redirectDefaultLanguageToRoot,
      }) =>
        languages.length > 1 &&
        !isMatchedIgnoredPaths(ignoredPaths, originalPath) ? (
          <LangSwitcherWrapper
            aria-label={messages[`accessibility.languageSwitch`]}
            className={className}
            role="navigation"
          >
            <LangSwitcher>
              {languages.map((language) => {
                const isCurrentLocale = currentLocale === language
                const url =
                  (redirectDefaultLanguageToRoot &&
                    defaultLanguage === language) ||
                  isMatchedIgnoredPaths(ignoredPaths, originalPath)
                    ? `${originalPath}${params}`
                    : `/${language}${originalPath}${params}`

                return (
                  <li key={language}>
                    {isCurrentLocale ? (
                      <CurrentLang
                        aria-label={
                          messages[`accessibility.languageSwitch.current`]
                        }
                        lang={language}
                        title={messages[`accessibility.languageSwitch.current`]}
                      >
                        {languageName[language]}
                      </CurrentLang>
                    ) : (
                      <LangLink
                        $active={isCurrentLocale ? 1 : 0}
                        aria-label={
                          messages[`accessibility.languageSwitch.${language}`]
                        }
                        lang={language}
                        to={url}
                        title={
                          messages[`accessibility.languageSwitch.${language}`]
                        }
                      >
                        {languageName[language]}
                      </LangLink>
                    )}
                  </li>
                )
              })}
            </LangSwitcher>
          </LangSwitcherWrapper>
        ) : null
      }
    </IntlContextConsumer>
  )
}

LanguageSwitcher.propTypes = {
  className: PropTypes.string,
}

export default LanguageSwitcher
